import { default as React, useCallback, useState } from "react";
import styled from "styled-components";

import type { FormTextInput } from "../../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { accountDeleteService, authenticationManager } from "../../../../../shared/core/service/services";
import {
  ACCOUNT_DELETE_MODAL_ID,
  CONTACT_SUPPORT_MODAL_ID,
  PINCODE_MUST_BE_CHANGED_MODAL_ID,
} from "../../../../core/modal/modal-id";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { BluePrimaryButton } from "../../../common/buttons/primary-button";
import { TextField } from "../../../common/data-forms/text-field";
import { Modal } from "../../../common/modal/modal";
import { WhiteModalContainer } from "../../../common/modal/white-modal-container";
import { StaticAssets } from "../../../styles/custom-assets";
import { theme } from "../../../styles/theme";
import { ContactSupportModal } from "../../contact-support/contact-support-modal";
import { PincodeChangeModal } from "../../pincode/pincode-change-modal";
import { OptionCard } from "./option-card";

enum DeletionStep {
  Warning,
  Confirm,
}

const ModalContent = ({
  step,
  formatMessage,
  goToContact,
  dismissModal,
  setStep,
  reason,
  setReason,
  handleDeleteAccount,
  feedback,
}) => {
  if (step === DeletionStep.Warning) {
    return (
      <>
        <Title>{formatMessage("accountDeleteScreen.title")}</Title>
        <Subtitle>{formatMessage("accountDeleteScreen.subtitle")}</Subtitle>
        <OptionsList>
          <OptionCard
            title={formatMessage("accountDeleteScreen.options.changeProfile.title")}
            subtitle={formatMessage("accountDeleteScreen.options.changeProfile.description")}
            onClick={() => {
              dismissModal();
              Modal.present(PINCODE_MUST_BE_CHANGED_MODAL_ID, () => <PincodeChangeModal type="skip-explanation" />, {
                canBeDismissed: false,
              });
            }}
          >
            <OptionIllustration src={StaticAssets.settingsPincode} alt="" />
          </OptionCard>
          <OptionCard
            title={formatMessage("accountDeleteScreen.options.unHappy.title")}
            subtitle={formatMessage("accountDeleteScreen.options.unHappy.description")}
            onClick={goToContact}
          >
            <OptionIllustration src={StaticAssets.settingsTalk} alt="" />
          </OptionCard>
        </OptionsList>
        <DeleteLink type="button" onClick={() => setStep(DeletionStep.Confirm)}>
          {formatMessage("accountDeleteScreen.button")}
        </DeleteLink>
        <Explanation>{formatMessage("accountDeleteScreen.explanation")}</Explanation>
      </>
    );
  }

  return (
    <>
      <StyledTitle>{formatMessage("accountDeleteScreen.confirm.title")}</StyledTitle>
      <ReasonField>
        <TextField
          input={reason}
          placeholder={formatMessage("accountDeleteScreen.confirm.placeholder")}
          onChange={(e) => setReason({ ...reason, value: e })}
          multiline
        />
      </ReasonField>
      {feedback.success || feedback.error ? (
        <Feedback $failed={!feedback.success}>{feedback.success || feedback.error}</Feedback>
      ) : (
        <>
          <StyledSubtitle>{formatMessage("accountDeleteScreen.confirm.subtitle")}</StyledSubtitle>
          <Warning>{formatMessage("accountDeleteScreen.confirm.warning")}</Warning>
        </>
      )}
      <StyledPrimaryButton size="S" onClick={handleDeleteAccount} disabled={!reason.value}>
        {formatMessage("accountDeleteScreen.confirm.button")}
      </StyledPrimaryButton>
    </>
  );
};

export const AccountDeleteModal = () => {
  const { formatMessage } = useIntl();
  const dismissModal = () => Modal.dismiss(ACCOUNT_DELETE_MODAL_ID);
  const [step, setStep] = useState<DeletionStep>(DeletionStep.Warning);
  const [feedback, setFeedback] = useState<{ success: string; error: string }>({ success: "", error: "" });
  const [reason, setReason] = useState<FormTextInput>({ id: "reason", value: "" });

  const goToContact = useCallback(() => {
    dismissModal();
    Modal.present(
      CONTACT_SUPPORT_MODAL_ID,
      () => (
        <ContactSupportModal
          title={formatMessage("contactSupport.notHappyTitle")}
          subtitle={formatMessage("contactSupport.notHappyMessage")}
        />
      ),
      { canBeDismissed: true },
    );
  }, [formatMessage]);

  const handleDeleteAccount = useCallback(() => {
    const session = authenticationManager.session.get();
    setFeedback({ success: "", error: "" });

    if (session.clientId && reason.value) {
      accountDeleteService
        .deleteAccount(session.clientId, reason.value)
        .then(() => {
          setFeedback({ success: formatMessage("accountDeleteScreen.confirm.success"), error: "" });
        })
        .catch((err) => {
          setFeedback({ success: "", error: err.message || "An error occurred" });
        });
    }
  }, [reason, formatMessage]);

  return (
    <ModalContainer closeButton id={ACCOUNT_DELETE_MODAL_ID} className="delete-account-modal-container">
      <ModalContent
        step={step}
        formatMessage={formatMessage}
        goToContact={goToContact}
        dismissModal={dismissModal}
        setStep={setStep}
        reason={reason}
        setReason={setReason}
        handleDeleteAccount={handleDeleteAccount}
        feedback={feedback}
      />
    </ModalContainer>
  );
};

const ModalContainer = styled(WhiteModalContainer)`
  &.delete-account-modal-container {
    margin-top: 5vh;
  }

  display: flex;
  flex-direction: column;
  padding: 56px 28px 28px;
  min-width: 400px;
`;

const Title = styled.h2`
  ${theme.boldText};
  margin: 0 0 8px;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Subtitle = styled.div`
  ${theme.text};
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
`;

const Warning = styled.div`
  ${theme.mediumText};
  margin: 8px 0 16px;
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
`;

/* The way through reads as a link, not as a button: it is the outcome nobody should fall into. */
const DeleteLink = styled(UnstyledButton)`
  ${theme.mediumText};
  align-self: flex-start;
  margin-top: 20px;
  font-size: 14px;
  line-height: 18px;
  text-decoration: underline;
  color: #741537;
`;

const Explanation = styled.div`
  ${theme.text};
  margin-top: 12px;
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const OptionIllustration = styled.img`
  width: 40px;
  height: 40px;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
`;

/* The shared field brings its own padded box; only the outline and the full width are missing. */
const ReasonField = styled.div`
  margin-bottom: 24px;

  > div {
    width: 100%;
    align-items: flex-start;
    border: 1px solid ${theme.colors.gray[300]};
    border-radius: 8px;
  }

  textarea {
    width: 100%;
    min-height: 96px;
    resize: vertical;
    ${theme.text};
    font-size: 15px;
    line-height: 20px;
    color: ${theme.colors.blue[800]};
  }
`;

const Feedback = styled.div<{ $failed: boolean }>`
  ${theme.text};
  margin-top: 8px;
  font-size: 15px;
  line-height: 20px;
  color: ${({ $failed }) => ($failed ? theme.colors.error[500] : theme.colors.success[500])};
`;

/* The modal's frame is not the box holding the content, so the width is set on the button. */
const StyledPrimaryButton = styled(BluePrimaryButton)`
  width: 100%;
  margin-top: 24px;
  padding: 13px;
  box-shadow: none;
`;

const StyledSubtitle = styled(Subtitle)`
  margin-bottom: 8px;
`;

const StyledTitle = styled(Title)`
  margin-bottom: 20px;
`;
